import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { ChangeEvent, KeyboardEvent } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Input } from '@opendoor/atoms/input';
import { Text } from '@opendoor/atoms/text';
import { useAtomsToastManager } from '@opendoor/atoms/toast';
import { cn } from '@opendoor/atoms/utils';
import { IconBookmark, IconCheck, IconPencil, IconTrash, IconX } from '@tabler/icons-react';

import type { SavedSearchFieldsFragment } from './__generated__/saved-searches.zeus';
import { describeSavedSearch, savedSearchFilterChips } from './presentation';
import { SAVED_SEARCH_MAP, useSavedSearchMapUrl } from './savedSearchMapUrl';
import { SavedSearchRibbon } from './SavedSearchRibbon';

/*
 * The saved search card, from the designer's Figma: a 208 x 102 tile with the
 * name and a filter count on the left and a map of the saved area fading in
 * from the right, under the bookmark ribbon.
 */
const SAVE_SEARCH_NAME_MAX_LENGTH = 80;
/** How long a card offers Undo after its ribbon is pulled, before the search is deleted. */
export const SAVED_SEARCH_UNDO_MS = 2000;
const RENAME_ERROR = 'Failed to rename search. Please try again.';

interface SavedSearchesMenuProps {
  savedSearches: SavedSearchFieldsFragment[];
  activeSavedSearchId?: string | null;
  onSelect: (savedSearch: SavedSearchFieldsFragment) => void;
  onRename: (
    savedSearch: SavedSearchFieldsFragment,
    newName: string,
  ) => Promise<{ success: boolean; errorMessage?: string | null }>;
  /** Asks to delete a search; the rows confirm it first. */
  onDelete?: (savedSearch: SavedSearchFieldsFragment) => void;
  /**
   * Deletes a search at once. A card calls it when its Undo window ends or
   * when it leaves the page.
   */
  onRemove?: (savedSearch: SavedSearchFieldsFragment) => Promise<{ success: boolean }>;
  /**
   * Runs as a removed card leaves with focus inside it, while focus is still
   * there, so the host can move it. Otherwise focus drops to the page.
   */
  onRemovedWithFocus?: () => void;
  onOpenFilters?: () => void;
  /**
   * `rows` lists compact rows, as in the saved searches dropdown. `cards`
   * shows each search as a card with its filters as chips.
   * @default 'rows'
   */
  layout?: 'rows' | 'cards';
}

export const SavedSearchesMenu = ({
  savedSearches,
  activeSavedSearchId,
  onSelect,
  onRename,
  onDelete,
  onRemove,
  onRemovedWithFocus,
  onOpenFilters,
  layout = 'rows',
}: SavedSearchesMenuProps) => {
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const handleStartRename = useCallback((savedSearch: SavedSearchFieldsFragment) => {
    setRenamingId(savedSearch.id);
  }, []);
  const handleEndRename = useCallback(() => {
    setRenamingId(null);
  }, []);

  if (savedSearches.length === 0 && layout === 'cards') {
    return <EmptySavedSearchesCard onOpenFilters={onOpenFilters} />;
  }

  if (savedSearches.length === 0) {
    return (
      <Col className="items-start gap-3 p-3">
        <Text as="p" className="text-label-md text-od-content-primary">
          No saved searches yet
        </Text>
        <Text as="p" className="text-body-sm text-od-content-secondary">
          Set your filters and location, then tap “Save search” to pick up right where you left off,
          on any device.
        </Text>
        {onOpenFilters && (
          <Button
            analyticsName="saved-searches-empty-open-filters"
            aria-label="Set filters"
            variant="secondary"
            size="sm"
            onClick={onOpenFilters}
          >
            Set filters
          </Button>
        )}
      </Col>
    );
  }

  const Item = layout === 'cards' ? SavedSearchCard : SavedSearchRow;
  if (layout === 'cards') {
    // As many columns as fit at 190px, then the cards share the row: three in
    // the search dropdown, more on a wider panel, always filling the width.
    return (
      <div className="grid grid-cols-[repeat(auto-fill,minmax(190px,1fr))] gap-3">
        {savedSearches.map((savedSearch) => (
          <Item
            key={savedSearch.id}
            savedSearch={savedSearch}
            isActive={savedSearch.id === activeSavedSearchId}
            isRenaming={renamingId === savedSearch.id}
            onStartRename={handleStartRename}
            onEndRename={handleEndRename}
            onSelect={onSelect}
            onRename={onRename}
            onDelete={onDelete}
            onRemove={onRemove}
            onRemovedWithFocus={onRemovedWithFocus}
          />
        ))}
      </div>
    );
  }
  return (
    <Col className="py-1">
      {savedSearches.map((savedSearch) => (
        <Item
          key={savedSearch.id}
          savedSearch={savedSearch}
          isActive={savedSearch.id === activeSavedSearchId}
          isRenaming={renamingId === savedSearch.id}
          onStartRename={handleStartRename}
          onEndRename={handleEndRename}
          onSelect={onSelect}
          onRename={onRename}
          onDelete={onDelete}
          onRemove={onRemove}
          onRemovedWithFocus={onRemovedWithFocus}
        />
      ))}
    </Col>
  );
};

interface SavedSearchRowProps {
  savedSearch: SavedSearchFieldsFragment;
  isActive: boolean;
  isRenaming: boolean;
  onStartRename: (savedSearch: SavedSearchFieldsFragment) => void;
  onEndRename: () => void;
  onSelect: (savedSearch: SavedSearchFieldsFragment) => void;
  onRename: (
    savedSearch: SavedSearchFieldsFragment,
    newName: string,
  ) => Promise<{ success: boolean; errorMessage?: string | null }>;
  onDelete?: (savedSearch: SavedSearchFieldsFragment) => void;
  onRemove?: (savedSearch: SavedSearchFieldsFragment) => Promise<{ success: boolean }>;
  onRemovedWithFocus?: () => void;
}

const useSavedSearchActions = ({
  savedSearch,
  isRenaming,
  onStartRename,
  onSelect,
  onDelete,
}: Pick<
  SavedSearchRowProps,
  'savedSearch' | 'isRenaming' | 'onStartRename' | 'onSelect' | 'onDelete'
>) => {
  const handleSelect = useCallback(() => {
    onSelect(savedSearch);
  }, [onSelect, savedSearch]);

  const handleDelete = useCallback(() => {
    onDelete?.(savedSearch);
  }, [onDelete, savedSearch]);

  const handleStartRename = useCallback(() => {
    onStartRename(savedSearch);
  }, [onStartRename, savedSearch]);

  const renameButtonRef = useRef<HTMLElement | null>(null);
  const wasRenamingRef = useRef(isRenaming);
  useEffect(() => {
    // A rename field that unmounts with focus inside drops focus to the page;
    // hand it back to the row unless another rename field already took it.
    const focusDropped = !document.activeElement || document.activeElement === document.body;
    if (wasRenamingRef.current && !isRenaming && focusDropped) {
      renameButtonRef.current?.focus({ preventScroll: true });
    }
    wasRenamingRef.current = isRenaming;
  }, [isRenaming]);

  return { handleSelect, handleDelete, handleStartRename, renameButtonRef };
};

const ActiveMarker = () => (
  <Row className="shrink-0 items-center gap-0.5 rounded-full bg-od-bg-hover-primary px-2 py-0.5">
    <IconCheck size={12} aria-hidden />
    <Text as="span" className="text-body-xs text-od-content-primary">
      Active
    </Text>
  </Row>
);

const SavedSearchRow = ({
  savedSearch,
  isActive,
  isRenaming,
  onStartRename,
  onEndRename,
  onSelect,
  onRename,
  onDelete,
}: SavedSearchRowProps) => {
  const { handleSelect, handleDelete, handleStartRename, renameButtonRef } = useSavedSearchActions({
    savedSearch,
    isRenaming,
    onStartRename,
    onSelect,
    onDelete,
  });

  if (isRenaming) {
    return <RenameRow savedSearch={savedSearch} onRename={onRename} onEndRename={onEndRename} />;
  }

  return (
    <Row className="group items-center gap-1 rounded-xl p-2 hover:bg-od-bg-hover-primary">
      <Box
        analyticsName="saved-search-select"
        aria-label={`Switch to saved search ${savedSearch.name}`}
        onClick={handleSelect}
        className="min-w-0 flex-1 flex-col items-start gap-0.5 py-1 text-left"
      >
        <Row className="w-full items-center gap-2">
          <Text as="p" className="line-clamp-1 text-body-md text-od-content-primary">
            {savedSearch.name}
          </Text>
          {isActive && <ActiveMarker />}
        </Row>
        <Text as="p" className="line-clamp-1 text-body-sm text-od-content-secondary">
          {describeSavedSearch(savedSearch)}
        </Text>
      </Box>
      <Row className="shrink-0 items-center">
        <Box
          ref={renameButtonRef}
          analyticsName="saved-search-rename"
          aria-label={`Rename ${savedSearch.name}`}
          onClick={handleStartRename}
          className="items-center justify-center rounded-lg p-2 hover:bg-od-bg-hover-tertiary"
        >
          <IconPencil size={18} aria-hidden />
        </Box>
        <Box
          analyticsName="saved-search-delete"
          aria-label={`Delete ${savedSearch.name}`}
          onClick={handleDelete}
          className="items-center justify-center rounded-lg p-2 hover:bg-od-bg-hover-tertiary"
        >
          <IconTrash size={18} aria-hidden />
        </Box>
      </Row>
    </Row>
  );
};

type RibbonState = 'saved' | 'pulling' | 'removed';

const SavedSearchCard = ({
  savedSearch,
  isActive,
  isRenaming,
  onStartRename,
  onEndRename,
  onSelect,
  onRename,
  onDelete,
  onRemove,
  onRemovedWithFocus,
}: SavedSearchRowProps) => {
  // The card has no rename control; the shared actions still keep its select handler.
  const { handleSelect } = useSavedSearchActions({
    savedSearch,
    isRenaming,
    onStartRename,
    onSelect,
    onDelete,
  });
  const toastManager = useAtomsToastManager();
  const filtersId = useId();
  const cardRef = useRef<HTMLElement | null>(null);
  const selectRef = useRef<HTMLElement | null>(null);
  const undoRef = useRef<HTMLElement | null>(null);
  const [ribbon, setRibbon] = useState<RibbonState>('saved');
  const [removeFailed, setRemoveFailed] = useState(false);
  // The delete went out, so Undo can no longer bring the search back.
  const [deleting, setDeleting] = useState(false);
  const ribbonStateRef = useRef<RibbonState>('saved');
  const undoTimerRef = useRef<number | undefined>(undefined);
  const committedRef = useRef(false);
  const mountedRef = useRef(true);
  // `useAtomsToastManager` returns a fresh object each render, so it goes through a ref.
  const latestRef = useRef({ savedSearch, onRemove, onRemovedWithFocus, toastManager });
  useEffect(() => {
    ribbonStateRef.current = ribbon;
    latestRef.current = { savedSearch, onRemove, onRemovedWithFocus, toastManager };
  });

  const commitRemoval = useCallback(() => {
    window.clearTimeout(undoTimerRef.current);
    if (committedRef.current) return;
    committedRef.current = true;
    if (mountedRef.current) setDeleting(true);
    const { savedSearch: removed, onRemove: remove } = latestRef.current;
    void remove?.(removed).then((result) => {
      if (result.success) return;
      committedRef.current = false;
      if (mountedRef.current) {
        setDeleting(false);
        setRemoveFailed(true);
        setRibbon('saved');
        return;
      }
      // The card left with the panel, so the page says what went wrong.
      latestRef.current.toastManager.add({
        title: `Couldn’t remove “${removed.name}”. Please try again.`,
        variant: 'error',
      });
    });
  }, []);

  // A card that leaves the page, as when the panel closes, deletes the search
  // whose ribbon it pulled: Undo is no longer on offer.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (ribbonStateRef.current !== 'saved') commitRemoval();
    };
  }, [commitRemoval]);

  // A card whose removal went through leaves the list, often with focus on its
  // Undo link. Removing a focused element raises no blur, so the host gets the
  // chance to move focus here: a layout cleanup runs while the card is still in
  // the page.
  useLayoutEffect(
    () => () => {
      if (committedRef.current && cardRef.current?.contains(document.activeElement)) {
        latestRef.current.onRemovedWithFocus?.();
      }
    },
    [],
  );

  useEffect(() => {
    if (ribbon === 'removed') undoRef.current?.focus({ preventScroll: true });
  }, [ribbon]);

  // A failed delete brings the ribbon back and takes Undo away. Focus that was
  // on Undo dropped to the page; give it to the card.
  useEffect(() => {
    if (!removeFailed) return;
    const active = document.activeElement;
    if (!active || active === document.body) selectRef.current?.focus({ preventScroll: true });
  }, [removeFailed]);

  const handleRibbonClick = useCallback(() => {
    setRemoveFailed(false);
    setRibbon((state) => (state === 'saved' ? 'pulling' : state));
  }, []);

  const handleRibbonPulled = useCallback(() => {
    setRibbon('removed');
    window.clearTimeout(undoTimerRef.current);
    undoTimerRef.current = window.setTimeout(commitRemoval, SAVED_SEARCH_UNDO_MS);
  }, [commitRemoval]);

  // A search on its way out can't be applied: a double click on the ribbon or a
  // click during Undo would apply it and then delete it.
  const removing = ribbon !== 'saved';
  const handleCardSelect = useCallback(() => {
    if (!removing) handleSelect();
  }, [handleSelect, removing]);

  const handleUndo = useCallback(() => {
    if (committedRef.current) return;
    window.clearTimeout(undoTimerRef.current);
    setRibbon('saved');
    // The Undo link goes away; keep focus on the card.
    selectRef.current?.focus({ preventScroll: true });
  }, []);

  const [hovered, setHovered] = useState(false);
  const handleHoverStart = useCallback(() => setHovered(true), []);
  const handleHoverEnd = useCallback(() => setHovered(false), []);

  if (isRenaming) {
    return (
      <Col className="rounded-xl border border-od-border-primary p-2">
        <RenameRow savedSearch={savedSearch} onRename={onRename} onEndRename={onEndRename} />
      </Col>
    );
  }

  const chips = savedSearchFilterChips(savedSearch);
  const filterCount = chips.length === 1 && chips[0] === 'All homes' ? 0 : chips.length;
  let pillLabel = 'All homes';
  if (filterCount === 1) pillLabel = '1 filter';
  else if (filterCount > 1) pillLabel = `${filterCount} filters`;
  return (
    <Col
      ref={cardRef}
      className="group relative h-[102px] w-full rounded-xl bg-[#FDFCF9] shadow-[inset_0_0_0_1px_#EAE8E0]"
      onPointerEnter={handleHoverStart}
      onPointerLeave={handleHoverEnd}
    >
      {/* Only the card's content dims while it is on its way out; the ribbon
          finishes its pull and Undo stays crisp above it. */}
      <div
        className={cn('absolute inset-0 rounded-xl transition-opacity', removing && 'opacity-60')}
      >
        <SavedSearchMapThumb savedSearch={savedSearch} hovered={hovered} />
        {/* The whole card applies the search; what it shows sits above this button
          and lets clicks through, except for its own controls. */}
        <Box
          ref={selectRef}
          // A click on a card on its way out does nothing, so it is not tracked either.
          analyticsName={removing ? undefined : 'saved-search-select'}
          aria-label={`Switch to saved search ${savedSearch.name}`}
          // A button's label replaces its content, so the filters are its description.
          aria-describedby={filtersId}
          aria-current={isActive ? 'true' : undefined}
          aria-disabled={removing || undefined}
          onClick={handleCardSelect}
          className={cn('absolute inset-0 rounded-xl', removing && 'cursor-default')}
        />
        <Col className="pointer-events-none relative items-start gap-3 p-3 pr-[101px]">
          {/* The card button already names the search and marks it current. */}
          <Text
            aria-hidden
            as="p"
            className={cn(
              'w-full min-w-0 truncate text-[14px] leading-[17.5px] font-normal tracking-[-0.014em]',
              removing ? 'text-[#89867F] line-through' : 'text-[#231E1B]',
            )}
          >
            {savedSearch.name}
          </Text>
          <Text
            aria-hidden
            as="span"
            className="inline-flex h-7 min-w-[61px] items-center justify-center rounded-[14px] bg-white px-2.5 text-[12px] leading-none font-medium tracking-[-0.03em] whitespace-nowrap text-[#89867F] shadow-[inset_0_0_0_1px_#EAE8E0]"
          >
            {pillLabel}
          </Text>
          {/* Read only as the card button's description. */}
          <span aria-hidden id={filtersId} className="sr-only">
            {chips.join(', ')}
          </span>
        </Col>
        <Col aria-live="polite" className="pointer-events-none absolute inset-x-0 bottom-0">
          {ribbon === 'removed' ? (
            <Text as="span" className="sr-only">
              Saved search removed
            </Text>
          ) : null}
          {removeFailed ? (
            <Text as="p" role="alert" className="px-3 pb-2 text-body-xs text-od-content-error">
              Couldn’t remove this search. Please try again.
            </Text>
          ) : null}
        </Col>
      </div>
      {ribbon === 'removed' ? (
        // Where the ribbon was, above its empty hit area.
        <Row className="absolute top-2.5 right-2 z-20 items-center gap-2">
          {deleting ? null : <UndoCountdown durationMs={SAVED_SEARCH_UNDO_MS} />}
          <Box
            ref={undoRef}
            // Stays in place once the delete went out, so focus stays with the
            // card until it leaves.
            analyticsName={deleting ? undefined : 'saved-search-undo-remove'}
            aria-label={`Undo removing ${savedSearch.name}`}
            aria-disabled={deleting || undefined}
            onClick={handleUndo}
            className={cn(
              'h-7 items-center rounded-lg px-2.5 text-label-sm text-[#231E1B] transition-opacity',
              deleting ? 'cursor-default opacity-40' : 'hover:bg-od-bg-hover-tertiary',
            )}
          >
            Undo
          </Box>
        </Row>
      ) : null}
      <SavedSearchRibbon
        label={`Remove ${savedSearch.name}`}
        tooltip="Remove saved search"
        pulled={ribbon !== 'saved'}
        onClick={handleRibbonClick}
        onPulled={handleRibbonPulled}
      />
    </Col>
  );
};

/** The saved area on a map, fading in from the card's top-right corner. */
const SavedSearchMapThumb = ({
  savedSearch,
  hovered,
}: {
  savedSearch: SavedSearchFieldsFragment;
  hovered: boolean;
}) => {
  const url = useSavedSearchMapUrl(savedSearch);
  const { image, offset, mask, restSaturation } = SAVED_SEARCH_MAP;
  const [failed, setFailed] = useState(false);
  const handleError = useCallback(() => setFailed(true), []);
  if (!url || failed) return null;
  const fade = `radial-gradient(${mask.rx}px ${mask.ry}px at ${mask.x}% ${mask.y}%, rgba(0, 0, 0, 1) ${mask.solidUntil}%, rgba(0, 0, 0, 0) 100%)`;
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute top-px right-px h-[100px] w-[101px] overflow-hidden rounded-r-[11px]"
      style={{
        maskImage: fade,
        WebkitMaskImage: fade,
        // Muted until the card is hovered, then the image's own colour.
        filter: `saturate(${hovered ? 1 : Math.max(0, (100 + restSaturation) / 100)})`,
        transition: 'filter 0.2s ease',
      }}
    >
      <img
        src={url}
        alt=""
        width={image.width}
        height={image.height}
        onError={handleError}
        style={{
          position: 'absolute',
          left: offset.x,
          top: offset.y,
          width: image.width,
          height: image.height,
          maxWidth: 'none',
        }}
      />
    </div>
  );
};

const UNDO_COUNTDOWN_SIZE = 18;
const UNDO_COUNTDOWN_RADIUS = 8;
const UNDO_COUNTDOWN_LENGTH = 2 * Math.PI * UNDO_COUNTDOWN_RADIUS;

/**
 * A ring as tall as Undo whose outline runs out over the Undo window, so the
 * visitor sees how long the search has left.
 */
const UndoCountdown = ({ durationMs }: { durationMs: number }) => {
  const arcRef = useRef<SVGCircleElement | null>(null);
  useEffect(() => {
    const arc = arcRef.current;
    if (!arc || typeof arc.animate !== 'function') return undefined;
    const animation = arc.animate(
      [{ strokeDashoffset: 0 }, { strokeDashoffset: UNDO_COUNTDOWN_LENGTH }],
      { duration: durationMs, easing: 'linear', fill: 'forwards' },
    );
    return () => animation.cancel();
  }, [durationMs]);
  return (
    <svg
      aria-hidden
      viewBox={`0 0 ${UNDO_COUNTDOWN_SIZE} ${UNDO_COUNTDOWN_SIZE}`}
      width={UNDO_COUNTDOWN_SIZE}
      height={UNDO_COUNTDOWN_SIZE}
      className="shrink-0 -rotate-90 text-[#231E1B]"
    >
      <circle
        ref={arcRef}
        cx={UNDO_COUNTDOWN_SIZE / 2}
        cy={UNDO_COUNTDOWN_SIZE / 2}
        r={UNDO_COUNTDOWN_RADIUS}
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeDasharray={UNDO_COUNTDOWN_LENGTH}
        strokeDashoffset={0}
      />
    </svg>
  );
};

const EmptySavedSearchesCard = ({ onOpenFilters }: { onOpenFilters?: () => void }) => (
  <Col className="items-start gap-2 rounded-2xl border border-dashed border-od-border-primary p-4">
    <IconBookmark aria-hidden size={20} className="text-od-content-secondary" />
    <Text as="p" className="text-label-md text-od-content-primary">
      No saved searches yet
    </Text>
    <Text as="p" className="text-body-sm text-od-content-secondary">
      Set your filters and location, then save the search to pick up where you left off, on any
      device.
    </Text>
    {onOpenFilters && (
      <Box
        analyticsName="saved-searches-empty-open-filters"
        aria-label="Set filters"
        onClick={onOpenFilters}
        className="text-label-sm text-od-content-primary underline underline-offset-4 hover:text-od-content-secondary"
      >
        Set filters
      </Box>
    )}
  </Col>
);

interface RenameRowProps {
  savedSearch: SavedSearchFieldsFragment;
  onRename: (
    savedSearch: SavedSearchFieldsFragment,
    newName: string,
  ) => Promise<{ success: boolean; errorMessage?: string | null }>;
  onEndRename: () => void;
}

const RenameRow = ({ savedSearch, onRename, onEndRename }: RenameRowProps) => {
  const [name, setName] = useState(savedSearch.name);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const handleNameChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  }, []);

  const handleCommit = useCallback(async () => {
    const trimmedName = name.trim();
    if (!trimmedName || submitting) {
      return;
    }
    if (trimmedName === savedSearch.name) {
      onEndRename();
      return;
    }
    setSubmitting(true);
    setErrorMessage(null);
    const result = await onRename(savedSearch, trimmedName);
    setSubmitting(false);
    if (result.success) {
      onEndRename();
      return;
    }
    setErrorMessage(result.errorMessage ?? RENAME_ERROR);
  }, [name, submitting, savedSearch, onRename, onEndRename]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        void handleCommit();
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        if (!submitting) {
          onEndRename();
        }
      }
    },
    [handleCommit, onEndRename, submitting],
  );

  return (
    <Col className="gap-1 rounded-xl p-2">
      <Row className="items-center gap-1">
        <Input
          ref={inputRef}
          size="sm"
          value={name}
          onChange={handleNameChange}
          onKeyDown={handleKeyDown}
          maxLength={SAVE_SEARCH_NAME_MAX_LENGTH}
          analyticsName="rename-saved-search-name-input"
          aria-label={`New name for ${savedSearch.name}`}
          // Read-only rather than disabled while saving: a disabled field drops focus
          // to the page, and a surrounding panel then closes.
          readOnly={submitting}
          className="min-w-0 flex-1"
        />
        <Box
          analyticsName="rename-saved-search-commit"
          aria-label="Save name"
          onClick={handleCommit}
          className={cn(
            'items-center justify-center rounded-lg p-2 hover:bg-od-bg-hover-tertiary',
            submitting && 'pointer-events-none opacity-50',
          )}
        >
          <IconCheck size={18} aria-hidden />
        </Box>
        <Box
          analyticsName="rename-saved-search-cancel"
          aria-label="Cancel rename"
          onClick={onEndRename}
          className={cn(
            'items-center justify-center rounded-lg p-2 hover:bg-od-bg-hover-tertiary',
            submitting && 'pointer-events-none opacity-50',
          )}
        >
          <IconX size={18} aria-hidden />
        </Box>
      </Row>
      {errorMessage && (
        <Text as="p" role="alert" className="text-body-sm text-od-content-error">
          {errorMessage}
        </Text>
      )}
    </Col>
  );
};
